import {
  Anchor,
  AppShell,
  Button,
  Card,
  Divider,
  Paper,
  Title,
  createTheme,
  type CSSVariablesResolver,
  type MantineColorsTuple,
} from "@mantine/core";

// Self-hosted IBM Plex families (see DESIGN_SPEC.md §2 and inertia.tsx imports).
const sansStack =
  '"IBM Plex Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
const monoStack =
  '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';

/*
 * Design tokens — see DESIGN.md (D4 accent, D5 buttons) and DESIGN_SPEC.md §2.
 *
 * The palette is a warm-neutral "paper on a desk" base with a single
 * Blueprint-blue accent. The accent lives in the blue family so it can never be
 * confused with the traffic-light status hues (green good / amber attention /
 * red over). Status colours are semantic ONLY — never repurpose them for brand.
 *
 * Raw hex tokens are exported on `theme.other` so downstream pages can reach the
 * exact editorial values (canvas/paper/ink/hairline/accent/status) that don't
 * map onto a Mantine colour tuple, in both light and dark schemes.
 */

// Blueprint blue — matched to the Sillplate logo: #1860AE (logo light) sits at
// shade 6 (light accent), #5EA1EA (logo dark) at shade 4 (dark accent).
const blueprint: MantineColorsTuple = [
  "#E7F0FB", // 0  accent tint bg
  "#D0E2F8", // 1
  "#B4D2F4", // 2  accent border (suggested-match card)
  "#8CBEF0", // 3  chart "planned" light line
  "#5EA1EA", // 4  ACCENT (dark scheme) — logo dark blue
  "#3F7EBD", // 5
  "#1860AE", // 6  ACCENT (primary, light scheme) — logo light blue
  "#134D8B", // 7  accent hover
  "#0F3C6C", // 8
  "#0B2B4E", // 9
];

// Positive / under / matched — green (semantic). Registered as `positive`.
const positive: MantineColorsTuple = [
  "#E7F4EE", // 0  tint bg
  "#CDE9DA", // 1
  "#A9D8BF", // 2
  "#7FC49F", // 3
  "#52AD7F", // 4
  "#2E9E68", // 5  line
  "#268056", // 6  text
  "#1F6B47", // 7
  "#185338", // 8
  "#113C28", // 9
];

// Warning / pending — amber (semantic).
const amber: MantineColorsTuple = [
  "#FBF1DF", // 0  tint bg
  "#F5E1BE", // 1
  "#EDCB8E", // 2
  "#E3B45C", // 3
  "#D89A2B", // 4  dot
  "#C4870F", // 5
  "#B0770F", // 6  text
  "#8F610C", // 7
  "#6E4A09", // 8
  "#4D3406", // 9
];

// Over / reject / error — red (semantic). Registered as `clay`.
const clay: MantineColorsTuple = [
  "#FBEDEA", // 0  tint bg
  "#F5D8D1", // 1
  "#EBB4A9", // 2
  "#E08F7F", // 3
  "#D46B56", // 4
  "#C85138", // 5
  "#C24632", // 6  text / main
  "#A23A29", // 7
  "#7F2D20", // 8
  "#5C2017", // 9
];

// Warm-neutral greys — the `sand` scale. Replaces Mantine's cool default `gray`
// (aliased below) so hairlines, dimmed text and borders all read warm.
// 0 = paper … 9 = ink (warm near-black).
const sand: MantineColorsTuple = [
  "#FAF9F6", // 0  paper
  "#F4F2EE", // 1  hairline (faint)
  "#EBE7E1", // 2  hairline
  "#DED9D1", // 3
  "#B5B0A8", // 4  faint / axis
  "#9A958E", // 5  muted / captions
  "#6F6A63", // 6  ink-soft / body-secondary
  "#514D47", // 7
  "#35322D", // 8
  "#1C1A17", // 9  ink (primary text)
];

// Warm dark scheme surfaces & text. Mantine uses `dark[7]` as the body
// background and `dark[6]` as the default border; the low indices are text.
const warmDark: MantineColorsTuple = [
  "#E9E5DF", // 0  brightest text
  "#CFCAC2", // 1
  "#B0AAA1", // 2
  "#918B82", // 3
  "#726C64", // 4  dimmed text
  "#55504A", // 5
  "#3D3934", // 6  borders
  "#2A2621", // 7  paper / surface (body bg)
  "#201D19", // 8  canvas / desk
  "#17140F", // 9
];

// The single source of truth for the app's content width (DESIGN_SPEC.md §2).
// The body pane now fills the full width of the content region (the area beside
// the sidebar) rather than sitting in a narrow centred column: every in-shell
// page renders its body at this width — the `LongPage` shell, the sticky
// `PageHeader` and the shared `PageContainer` all read it — so the header and
// body stay edge-aligned as you navigate between pages. The horizontal `px`
// gutter on each container keeps content off the very edge.
export const CONTENT_MAX_WIDTH = "100%";

// Editorial tokens that don't fit a Mantine tuple. Downstream pages read these
// off `theme.other.*`; the scheme-reactive ones are also exposed as CSS custom
// properties (see `cssVariablesResolver`) as `--app-*`.
const otherTokens = {
  light: {
    // Retired as the body background (the app is now a flat paper surface);
    // kept as a reference value only. See cssVariablesResolver below.
    canvas: "#EFEDE8",
    paper: "#FAF9F6",
    card: "#FFFFFF",
    ink: "#1C1A17",
    inkSoft: "#6F6A63",
    muted: "#9A958E",
    faint: "#B5B0A8",
    hairline: "#EBE7E1",
    hairlineSoft: "#F0EEE9",
    hairlineFaint: "#F4F2EE",
    surfaceBorder: "#E4E0D9",
  },
  dark: {
    canvas: "#201D19",
    paper: "#2A2621",
    card: "#302C27",
    ink: "#E9E5DF",
    inkSoft: "#B0AAA1",
    muted: "#918B82",
    faint: "#726C64",
    hairline: "#3D3934",
    hairlineSoft: "#332F2A",
    hairlineFaint: "#2A2621",
    surfaceBorder: "#3D3934",
  },
  accent: {
    base: "#1860AE",
    hover: "#134D8B",
    tint: "#E7F0FB",
    border: "#B4D2F4",
    chartLight: "#8CBEF0",
  },
  // Semantic status — never repurpose for branding (DESIGN.md D3/D4).
  status: {
    positive: { text: "#268056", line: "#2E9E68", tint: "#E7F4EE" },
    onTrack: { text: "#1860AE", tint: "#E7F0FB" },
    warning: { text: "#B0770F", dot: "#D89A2B", tint: "#FBF1DF" },
    over: { text: "#C24632", tint: "#FBEDEA" },
  },
  // Page/layout rhythm (DESIGN_SPEC.md §2 "Shape & depth").
  layout: {
    contentMaxWidth: CONTENT_MAX_WIDTH,
    sectionGap: 44,
    cardShadow: "0 1px 2px rgba(28,26,23,0.03)",
    windowShadow: "0 30px 70px -30px rgba(28,26,23,0.30)",
  },
} as const;

export const theme = createTheme({
  primaryColor: "blueprint",
  // Accent at shade 6 (#1860AE) in light; shade 4 (#5EA1EA) in dark — the two
  // logo blues — so the accent tracks the brand mark in each scheme.
  primaryShade: { light: 6, dark: 4 },
  autoContrast: true,
  luminanceThreshold: 0.4,

  colors: {
    blueprint,
    positive,
    amber,
    clay,
    sand,
    // Alias `sand` onto Mantine's `gray` slot too, so any stock component that
    // reaches for `gray` stays warm rather than falling back to cool default.
    gray: sand,
    dark: warmDark,
  },

  white: "#FFFFFF",
  black: "#1C1A17",

  // Radius: buttons 10 (md default), cards 18 (xl), pills use 999 inline.
  defaultRadius: "md",
  radius: {
    xs: "6px",
    sm: "8px",
    md: "10px",
    lg: "14px",
    xl: "18px",
  },

  // Airy spacing — section gap ≈ xl (44px). (DESIGN_SPEC.md §2.)
  spacing: {
    xs: "8px",
    sm: "12px",
    md: "18px",
    lg: "28px",
    xl: "44px",
  },

  fontFamily: sansStack,
  fontFamilyMonospace: monoStack,

  headings: {
    fontFamily: sansStack,
    fontWeight: "700",
    textWrap: "pretty",
    sizes: {
      h1: { fontSize: "2rem", lineHeight: "1.15" }, // ~32px big page title
      h2: { fontSize: "1.1875rem", lineHeight: "1.3" }, // ~19px section heading
      h3: { fontSize: "1.0625rem", lineHeight: "1.35" }, // ~17px
      h4: { fontSize: "0.95rem", lineHeight: "1.4" },
    },
  },

  other: otherTokens,

  components: {
    // Every AppShell border (navbar right-edge, header bottom-edge, aside,
    // footer) uses the warm hairline. Mantine defaults `--app-shell-border-color`
    // to the cool `gray-3` *on the AppShell root element itself*, so overriding
    // it at `:root` (via cssVariablesResolver) loses to inheritance proximity —
    // it has to be set on the element. `--app-hairline` keeps it scheme-aware
    // (light rgb(235,231,225) / dark equivalent).
    AppShell: AppShell.extend({
      styles: { root: { "--app-shell-border-color": "var(--app-hairline)" } },
    }),

    // Headings get the tight editorial tracking (DESIGN_SPEC.md §2 Type).
    Title: Title.extend({
      styles: { root: { letterSpacing: "-0.02em" } },
    }),

    // Button hierarchy (DESIGN.md D5): exactly one filled primary per screen;
    // white secondary; quiet accent-text tertiary.
    //   variant="filled"  → the single accent primary (rare)
    //   variant="default" → white surface, warm 1px border  (secondary)
    //   variant="subtle"  → accent text, no fill/border      (quiet/tertiary)
    Button: Button.extend({
      defaultProps: { radius: "md" },
      styles: (_theme, props) => {
        if (props.variant === "default") {
          return {
            root: {
              borderColor: "light-dark(#E4E0D9, #3D3934)",
              backgroundColor: "light-dark(#FFFFFF, #302C27)",
            },
          };
        }
        return { root: {} };
      },
    }),

    // Cards: 18px radius, warm hairline border, barely-there shadow.
    Card: Card.extend({
      defaultProps: { radius: "xl", withBorder: true },
      styles: {
        root: {
          backgroundColor: "light-dark(#FFFFFF, #302C27)",
          borderColor: "light-dark(#EBE7E1, #3D3934)",
          boxShadow: otherTokens.layout.cardShadow,
        },
      },
    }),

    // Paper surfaces (panels) share the card radius; border opt-in per usage.
    Paper: Paper.extend({
      defaultProps: { radius: "lg" },
    }),

    // Links use the accent and go darker on hover (DESIGN_SPEC.md §2 colour).
    Anchor: Anchor.extend({
      defaultProps: { fw: 500 },
    }),

    // Dividers share the same warm hairline as every other rule in the app
    // (AppShell borders included — see cssVariablesResolver's
    // `--app-shell-border-color`). Wired to `--app-hairline` so it tracks the
    // active colour scheme rather than Mantine's cool default border.
    Divider: Divider.extend({
      styles: { root: { "--divider-color": "var(--app-hairline)" } },
    }),
  },
});

/*
 * Scheme-reactive CSS custom properties. Wired into MantineProvider (see
 * inertia.tsx) so the warm paper/ink/hairline tokens are available as `--app-*`
 * in whichever scheme is active, and so the page body itself uses warm paper
 * rather than plain white/near-black.
 *
 * NOTE: the app background is the single flat "paper" surface (`sand.0`,
 * #FAF9F6). The darker "desk" canvas (#EFEDE8) is retired as the body
 * background — the token is kept below purely as a reference value; nothing
 * paints with it anymore. Cards sit on paper as plain white (#FFFFFF).
 */
export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {},
  light: {
    "--mantine-color-body": otherTokens.light.paper,
    "--mantine-color-text": otherTokens.light.ink,
    "--app-canvas": otherTokens.light.canvas,
    "--app-paper": otherTokens.light.paper,
    "--app-card": otherTokens.light.card,
    "--app-ink": otherTokens.light.ink,
    "--app-ink-soft": otherTokens.light.inkSoft,
    "--app-muted": otherTokens.light.muted,
    "--app-faint": otherTokens.light.faint,
    "--app-hairline": otherTokens.light.hairline,
    "--app-hairline-soft": otherTokens.light.hairlineSoft,
    "--app-hairline-faint": otherTokens.light.hairlineFaint,
    "--app-surface-border": otherTokens.light.surfaceBorder,
    "--app-accent": otherTokens.accent.base,
    "--app-accent-hover": otherTokens.accent.hover,
    "--app-accent-tint": otherTokens.accent.tint,
    "--app-accent-border": otherTokens.accent.border,
  },
  dark: {
    "--mantine-color-body": otherTokens.dark.paper,
    "--mantine-color-text": otherTokens.dark.ink,
    "--app-canvas": otherTokens.dark.canvas,
    "--app-paper": otherTokens.dark.paper,
    "--app-card": otherTokens.dark.card,
    "--app-ink": otherTokens.dark.ink,
    "--app-ink-soft": otherTokens.dark.inkSoft,
    "--app-muted": otherTokens.dark.muted,
    "--app-faint": otherTokens.dark.faint,
    "--app-hairline": otherTokens.dark.hairline,
    "--app-hairline-soft": otherTokens.dark.hairlineSoft,
    "--app-hairline-faint": otherTokens.dark.hairlineFaint,
    "--app-surface-border": otherTokens.dark.surfaceBorder,
    "--app-accent": "#5EA1EA",
    "--app-accent-hover": "#8CBEF0",
    "--app-accent-tint": "#26231F",
    "--app-accent-border": "#3D4A63",
  },
});
